<div class="form-page-container">
  <div class="container-flex-space-between">
    <app-page-title [title]="translations.title"></app-page-title>

    @if (!isLoading()) {
      @if (isPrintView()) {
        <button
          #printButton
          class="hide"
          [useExistingCss]="true"
          [printStyle]="printStyle"
          [printSectionId]="printSectionId"
          ngxPrint
        >
          print
        </button>

        <app-button [disabled]="!isReadyToPrint()" (pressed)="printButton.click()">
          <ng-container *ngTemplateOutlet="printButtonTemplate"></ng-container>
        </app-button>
      } @else {
        @if (isDeliveryReportPrintEnabled) {
          <app-button (pressed)="onNavigateToPrintable()">
            <mat-icon icon-left>print</mat-icon>
            <ng-container i18n="@@report.printpreview">Print preview</ng-container>
          </app-button>
        }
      }
    }
  </div>

  @if (isLoading()) {
    <app-loader></app-loader>
  } @else {
    <div [id]="printSectionId">
      <div class="main-delivery-info">
        <div class="header-container">
          <h1>{{ delivery.deliveryId }}</h1>
        </div>

        <div class="information">
          <div class="column">
            <div class="header" i18n="@@shared.status">Status</div>
            <div class="value">{{ statusDisplayText() }}</div>
          </div>

          <div class="column">
            <div class="header" i18n="@@delivery-report.sentdate">Sent date</div>
            <div class="value">{{ delivery.sent ? (delivery.sent | appDate) : "-" }}</div>
          </div>

          <div class="column">
            <div class="header" i18n="@@delivery-report.delivereddate">Delivered date</div>
            <div class="value">{{ delivery.delivered ? (delivery.delivered | appDate) : "-" }}</div>
          </div>
        </div>
      </div>

      <div class="element-content element-content-padded no-bottom-margin">
        <div class="sections flex-column">
          @if (printSectionFormGroupValue.includeMainLocationsInfo) {
            <section>
              <app-delivery-report-main-locations-table
                [sender]="sender"
                [receiver]="receiver"
              ></app-delivery-report-main-locations-table>
            </section>
          }

          @if (printSectionFormGroupValue.includeTraderInfo && delivery.agents.length) {
            <section>
              <h3 i18n="@@delivery-report.traderinformation">Trader information</h3>

              <app-connections-table
                [connections]="traders"
                [subscribeToOverlayRefreshTable]="false"
                [isRecordStateFilterEnabled]="false"
                [isSearchEnabled]="false"
                [isPaginatorEnabled]="false"
                [isPrintView]="isPrintView()"
                [columns]="[
                  'name',
                  'address.countryName',
                  'address.street',
                  'address.zipCode',
                  'address.region',
                  'certificates',
                  'eori',
                  'email',
                ]"
              ></app-connections-table>
            </section>
          }

          @if (printSectionFormGroupValue.includeProductInfo) {
            <section>
              <h3 i18n="@@delivery-report.productinformation">Product information</h3>

              <app-quantity-summary-table
                [isSearchEnabled]="false"
                [isPaginatorEnabled]="false"
                [allProducts]="allProducts"
                [allMaterials]="allMaterials"
                [allUnitOfMeasurements]="allUnitOfMeasurements()"
                [selectedItems]="items"
                [isPrintView]="isPrintView()"
                [columns]="[
                  'productName',
                  'materialNames',
                  'materialData',
                  'totalQuantity',
                  'customFields',
                ]"
              ></app-quantity-summary-table>
            </section>
          }

          @if (
            printSectionFormGroupValue.includeAdditionalInfo && customDeliveryFieldsValues?.length
          ) {
            <section>
              <h3>{{ additionalInformationText }}</h3>

              <div class="information grid">
                @for (
                  customFieldValue of customDeliveryFieldsValues;
                  track customFieldValue.label
                ) {
                  <div class="column">
                    <div class="header">{{ customFieldValue.label }}</div>
                    <div class="value">{{ customFieldValue.value }}</div>
                  </div>
                }
              </div>
            </section>
          }

          @if (documentTypes.length) {
            <section>
              <h3 i18n="@@delivery-report.deliverydocuments">Delivery documents</h3>

              <app-document-types-table
                [documentTypes]="documentTypes"
                [areButtonsEnabled]="false"
                [isRecordStateFilterEnabled]="false"
                [isPaginatorEnabled]="false"
                [isSearchEnabled]="false"
                [isPrintView]="isPrintView()"
                [columns]="['documentType', 'documentNames']"
              ></app-document-types-table>
            </section>
          }

          @if (printSectionFormGroupValue.includeProductTransformations) {
            @if (isLoadingSupplyChainInfo()) {
              <app-loader></app-loader>
            } @else {
              <app-delivery-report-processes
                [itemIds]="deliveryItemIds"
                [isPrintView]="isPrintView()"
              ></app-delivery-report-processes>
            }
          }

          @if (!this.isPrintView()) {
            <section>
              <h3 i18n="@@delivery-report.supplychaininformation">Supply chain information</h3>

              @if (isLoadingSupplyChainInfo()) {
                <app-loader></app-loader>
              } @else {
                <mat-tab-group animationDuration="0ms" class="full-width">
                  <mat-tab>
                    <ng-template mat-tab-label i18n="@@shared.graph">Graph</ng-template>
                    <app-items-supply-chain
                      [allProcessTypes]="allProcessTypes"
                      [heightFn]="supplyChainHeightFn"
                      [itemIds]="deliveryItemIds"
                      [supplyChainItems]="supplyChainItems"
                      [isZoomPromptEnabled]="true"
                      svgContentClass="unset-height"
                    ></app-items-supply-chain>
                  </mat-tab>
                  <mat-tab>
                    <ng-template mat-tab-label i18n="@@shared.map">Map</ng-template>
                    <app-items-supply-chain-map
                      [itemIds]="deliveryItemIds"
                      [delivery]="deliveryDetails"
                      [isZoomPromptEnabled]="true"
                    />
                  </mat-tab>
                </mat-tab-group>
              }
            </section>
          }

          @if (printSectionFormGroupValue.includeSupplyChainEntities) {
            <section>
              <h3 i18n="@@delivery-report.supplychainentities">Supply chain entities</h3>

              @if (isLoadingSupplyChainInfo()) {
                <app-loader></app-loader>
              } @else {
                <app-delivery-report-supply-chain-entities
                  [supplyChainItems]="supplyChainItems"
                ></app-delivery-report-supply-chain-entities>
              }
            </section>
          }

          @if (printSectionFormGroupValue.includeLocationInfo) {
            <section>
              <h3 i18n="@@delivery-report.locationinformation">Location information</h3>

              @if (isLoadingSupplyChainInfo()) {
                <app-loader></app-loader>
              } @else {
                <app-locations-table
                  [locations]="supplyChainLocations"
                  [isRecordStateFilterEnabled]="false"
                  [isPaginatorEnabled]="false"
                  [isSearchEnabled]="false"
                  [subscribeToOverlayRefreshTable]="false"
                  [isPrintView]="isPrintView()"
                  [columns]="[
                    'name',
                    'types',
                    'organisationName',
                    'address.countryName',
                    'address.street',
                    'address.zipCode',
                    'certificates',
                  ]"
                ></app-locations-table>
              }
            </section>
          }

          @if (printSectionFormGroupValue.includeLocationDetails) {
            <section>
              <h3 i18n="@@shared.locationdetails">Location details</h3>

              @if (isLoadingSupplyChainInfo()) {
                <app-loader></app-loader>
              } @else {
                <app-delivery-report-location-details
                  [locations]="supplyChainLocations"
                  [isPrintView]="isPrintView()"
                ></app-delivery-report-location-details>
              }
            </section>
          }

          @if (printSectionFormGroupValue.includeOrganisationCertificatesDocuments) {
            <section>
              <h3 i18n="@@delivery-report.organisationcertificatesdocuments">
                Organisation certificates & documents
              </h3>

              @if (isLoadingSupplyChainInfo()) {
                <app-loader></app-loader>
              } @else {
                <app-delivery-report-organisation-certificates-documents
                  [locations]="supplyChainLocations"
                  [isPrintView]="isPrintView()"
                ></app-delivery-report-organisation-certificates-documents>
              }
            </section>
          }

          @if (printSectionFormGroupValue.includeOtherDeliveries) {
            <section>
              <h3 i18n="@@delivery-report.otherdeliveriesinthissupplychain">
                Other deliveries in this supply chain
              </h3>

              @if (isLoadingSupplyChainInfo()) {
                <app-loader></app-loader>
              } @else {
                <app-delivery-report-other-deliveries
                  [deliveries]="supplyChainDeliveries"
                  [isPrintView]="isPrintView()"
                ></app-delivery-report-other-deliveries>
              }
            </section>
          }
        </div>
      </div>
    </div>
  }
</div>

<ng-template #printButtonTemplate>
  @if (!isLoading()) {
    @if (isReadyToPrint()) {
      <mat-icon icon-left>print</mat-icon>
      <ng-container i18n="@@report.print">Print</ng-container>
    } @else {
      <ng-container i18n="@@report.loadingprintdata">Loading data for printing...</ng-container>
    }
  }
</ng-template>

@if (!isLoading() && isRegularMode()) {
  <ng-template #printOptionsTemplate>
    <div class="flex-column" [formGroup]="printSectionFormGroup">
      <app-checkbox
        [label]="translations.mainLocationsInfo"
        formControlName="includeMainLocationsInfo"
      ></app-checkbox>
      <app-checkbox
        [label]="translations.traderInfo"
        formControlName="includeTraderInfo"
      ></app-checkbox>
      <app-checkbox
        [label]="translations.productInfo"
        formControlName="includeProductInfo"
      ></app-checkbox>
      <app-checkbox
        [label]="translations.additionalInfo"
        formControlName="includeAdditionalInfo"
      ></app-checkbox>
      <app-checkbox
        [label]="translations.productTransformations"
        formControlName="includeProductTransformations"
      ></app-checkbox>
      <app-checkbox
        [label]="translations.supplyChainEntities"
        formControlName="includeSupplyChainEntities"
      ></app-checkbox>
      <app-checkbox
        [label]="translations.locationInfo"
        formControlName="includeLocationInfo"
      ></app-checkbox>
      <app-checkbox
        [label]="translations.locationDetails"
        formControlName="includeLocationDetails"
      ></app-checkbox>
      <app-checkbox
        [label]="translations.organisationCertificatesDocuments"
        formControlName="includeOrganisationCertificatesDocuments"
      ></app-checkbox>
      <app-checkbox
        [label]="translations.otherDeliveries"
        formControlName="includeOtherDeliveries"
      ></app-checkbox>
    </div>
  </ng-template>
}
